import { GoogleOAuthProvider } from "@react-oauth/google";
import { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Link } from "react-router-dom";
import { HiOutlineEye, HiOutlineEyeSlash, HiOutlineEnvelope, HiOutlineLockClosed } from "react-icons/hi2";
import logo from "../assets/logo.png";
import FacebookLoginButton from "../components/FacebookLoginButton";
import GoogleLoginButton from "../components/GoogleLoginButton";
import { GOOGLE_CLIENT_ID, RECAPTCHA_SITE_KEY } from "../config";
import { requestNotificationPermission } from "../firebase";
import Api from "../service/http";

const SigninForm = () => {
  const params = new URLSearchParams(window.location.search);
  const redirect = params.get("redirect");
  const tag = params.get("tag");

  const [isLoggedIn, setIsLoggedIn] = useState(
    localStorage.getItem("accessToken") ? true : false
  );
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  if (isLoggedIn) {
    let url = "/profile";
    if (redirect) {
      url = redirect;
      if (tag) {
        url += `#${tag}`;
      }
    }
    window.location.href = url;
  }

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    remember: true,
    showPassword: false,
  });
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const togglePasswordVisibility = () => {
    setFormData((prev) => ({
      ...prev,
      showPassword: !prev.showPassword,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleLogin({ ...formData, type: "manual" });
  };

  const handleLogin = async (data) => {
    if (!captchaToken) {
      alert("Please verify reCAPTCHA.");
      return;
    }
    setLoading(true);
    const response = await Api.post("/web/login", data);
    if (response.status === 200) {
      localStorage.clear();
      localStorage.setItem("accessToken", JSON.stringify(response.data.access));
      localStorage.setItem("user", JSON.stringify(response.data.user));
      let url = "/profile";
      if (redirect) {
        url = redirect;
        if (tag) {
          url += `#${tag}`;
        }
      }
      window.location.href = url;
    } else {
      setError(response?.data?.message || "Login failed");
    }
    setLoading(false);
  };

  const handleFacebookLogin = async (response) => {
    if (!captchaToken) {
      alert("Please verify reCAPTCHA.");
      return;
    }

    if (!response.userID) {
      console.log("Facebook login failed");
      return;
    }

    handleLogin({
      id: response?.userID,
      email: response?.email,
      name: response?.name,
      photo: response?.picture?.data?.url,
      type: "facebook",
    });
  }

  const handleGoogleLogin = async (codeResponse) => {
    if (!captchaToken) {
      alert("Please verify reCAPTCHA.");
      return;
    }
    
    try {
      setLoading(true);
      // When using useGoogleLogin, we receive an access token.
      // We fetch user info from Google's userinfo endpoint.
      const res = await fetch(`https://www.googleapis.com/oauth2/v3/userinfo?access_token=${codeResponse.access_token}`);
      if (!res.ok) throw new Error('Failed to fetch user info');
      const decoded = await res.json();
      
      handleLogin({
        id: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        photo: decoded.picture,
        type: "google",
      });
    } catch (error) {
      console.error("Google Info Error:", error);
      setError("Failed to get Google user info. Please try again.");
      setLoading(false);
    }
  }

  useEffect(() => {
    requestNotificationPermission();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8f7fa] dark:bg-[#25293c] p-4">
      <div className="w-full max-w-md bg-white dark:bg-[#2f3349] rounded-2xl shadow-xl overflow-hidden transition-all duration-300 flex flex-col my-auto">
        <div className="p-6 sm:p-8">
          {/* Logo & Brand */}
          <Link to="/" className="flex flex-col items-center mb-6 hover:opacity-80 transition-opacity">
            <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-500/10 rounded-2xl flex items-center justify-center mb-3 transition-colors">
              <img src={logo} alt="Logo" className="h-8 w-8 object-contain" />
            </div>
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">Welcome Back! 👋</h1>
          </Link>
          <div className="flex flex-col items-center mb-6 -mt-4 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Please sign-in to your account</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl flex items-center gap-3 animate-shake">
              <div className="w-2 h-2 rounded-full bg-red-500"></div>
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Field */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-1">Email</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-500 transition-colors">
                  <HiOutlineEnvelope size={20} />
                </div>
                <input
                  type="email"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="name@example.com"
                  className="w-full pl-11 pr-4 py-2.5 bg-gray-50 dark:bg-[#25293c] border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all placeholder:text-gray-400"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between px-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
                <Link to="/forget-password" size="sm" className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors">
                  Forgot?
                </Link>
              </div>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-500 transition-colors">
                  <HiOutlineLockClosed size={20} />
                </div>
                <input
                  type={formData.showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full pl-11 pr-12 py-2.5 bg-gray-50 dark:bg-[#25293c] border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all placeholder:text-gray-400"
                  required
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  {formData.showPassword ? <HiOutlineEyeSlash size={20} /> : <HiOutlineEye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-center px-1">
              <label className="flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  name="remember"
                  checked={formData.remember}
                  onChange={handleChange}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:bg-[#25293c] dark:border-gray-700 transition-all cursor-pointer"
                />
                <span className="ml-2.5 text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors">Remember me</span>
              </label>
            </div>

            {/* reCAPTCHA */}
            <div className="flex justify-center py-2 overflow-hidden scale-90 sm:scale-100 origin-center">
              <ReCAPTCHA
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={handleCaptchaChange}
                theme="light"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white py-3 rounded-xl font-bold shadow-lg shadow-indigo-200 dark:shadow-none transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                "Sign In"
              )}
            </button>

            {/* Social Logins */}
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-[10px] uppercase">
                <span className="px-3 bg-white dark:bg-[#2f3349] text-gray-400 font-medium tracking-wider">or continue with</span>
              </div>
            </div>

            <div className="flex justify-center items-center gap-4">
              <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                <GoogleLoginButton handleLogin={handleGoogleLogin} />
              </GoogleOAuthProvider>
              <FacebookLoginButton handleLogin={handleFacebookLogin} />
            </div>

            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
              New on our platform?{" "}
              <Link to="/signup" className="font-bold text-indigo-600 dark:text-indigo-400 hover:underline">
                Create an account
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SigninForm;
